import React, { useState } from 'react';

import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Box, Button, Stack, Typography } from '@mui/material';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import { UI, cardSx, dateShort, value } from './operationalUi';

export interface WeekPoint {
  week: string;
  partial: boolean;
  margin: number | null;
  marginEx: number | null;
  campaignDays: number | null;
  campaignDaysEx: number | null;
  marginPerDay: number | null;
  marginPerDayEx: number | null;
}
export default function WeeklyTrend({
  weeks,
  action,
  hasExclusions = false,
  onExport,
}: {
  weeks: WeekPoint[];
  action?: React.ReactNode;
  hasExclusions?: boolean;
  onExport?: () => void;
}) {
  const [readout, setReadout] = useState<HTMLElement | null>(null);
  const [activeChart, setActiveChart] = useState<string | null>(null);
  const charts = [
    { title: 'Margin per week', field: 'margin', ex: 'marginEx', money: true },
    { title: 'Recorded campaign-days', field: 'campaignDays', ex: 'campaignDaysEx', money: false },
    { title: 'Margin per campaign-day', field: 'marginPerDay', ex: 'marginPerDayEx', money: true },
  ] as const;
  return (
    <Box sx={{ ...cardSx, p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.teal, letterSpacing: 0.6 }}>
          LAST 12 WEEKS
        </Typography>
        <Box sx={{ flex: 1 }} />
        {action}
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
          gap: 1,
          flex: 1,
          minHeight: 80,
          '@container (max-width:600px)': {
            gridTemplateColumns: 'minmax(0,1fr)',
            gridTemplateRows: 'repeat(3,minmax(120px,1fr))',
          },
        }}
      >
        {charts.map((chart) => (
          <Box
            key={chart.field}
            onMouseEnter={() => setActiveChart(chart.field)}
            onMouseLeave={() => setActiveChart(null)}
            onFocusCapture={() => setActiveChart(chart.field)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setActiveChart(null);
            }}
            sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>{chart.title}</Typography>
            <Box sx={{ flex: 1, minHeight: 70 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeks} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
                  <CartesianGrid vertical={false} stroke={UI.line} />
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 10, fill: UI.muted }}
                    tickFormatter={dateShort}
                    interval="preserveStartEnd"
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    width={chart.money ? 52 : 40}
                    tick={{ fontSize: 10, fill: UI.muted }}
                    tickFormatter={(v) =>
                      `${chart.money ? 'A$' : ''}${Math.abs(v) >= 1e6 ? (v / 1e6).toFixed(0) + 'M' : Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'k' : Math.round(v)}`
                    }
                    axisLine={false}
                    tickLine={false}
                  />
                  <ReportChartTooltip
                    portal={readout}
                    active={readout && activeChart === chart.field ? undefined : false}
                    position={{ x: 0, y: 0 }}
                    isAnimationActive={false}
                    wrapperStyle={{ position: 'absolute', top: 0, left: 0, width: '100%', pointerEvents: 'none' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const point = payload[0].payload as WeekPoint;
                      return (
                        <Box sx={{ bgcolor: UI.paper, minHeight: 36, fontSize: 12, lineHeight: 1.4 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                            {chart.title} · {dateShort(String(label))}
                            {point.partial ? ' · partial week' : ''}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: UI.teal }}>
                            {hasExclusions ? 'With exclusions' : 'All Flash'}{' '}
                            {value(point[hasExclusions ? chart.ex : chart.field], chart.money ? 'currency' : '')}
                          </Typography>
                        </Box>
                      );
                    }}
                  />
                  <Line
                    dataKey={hasExclusions ? chart.ex : chart.field}
                    name={hasExclusions ? 'With exclusions' : 'All Flash'}
                    stroke={UI.teal}
                    strokeWidth={2}
                    dot={(props) =>
                      props.payload.partial ? (
                        <circle key={props.key} cx={props.cx} cy={props.cy} r="3" fill={UI.paper} stroke={UI.teal} />
                      ) : (
                        <circle key={props.key} r="0" />
                      )
                    }
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        ))}
      </Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, flexWrap: 'wrap', rowGap: 1 }}>
        <Box
          ref={setReadout}
          data-testid="flash-weekly-readout"
          sx={{ position: 'relative', minHeight: 36, flex: 1, minWidth: 0 }}
        >
          <Typography sx={{ fontSize: 12, color: UI.muted, visibility: activeChart ? 'hidden' : 'visible' }}>
            {hasExclusions ? 'With exclusions' : 'All Flash'} · Hollow: partial week
          </Typography>
        </Box>
        {onExport && (
          <Button size="small" onClick={onExport} sx={{ fontSize: 12, p: 0, minWidth: 0 }}>
            Export
          </Button>
        )}
      </Stack>
    </Box>
  );
}
